import R from 'ramda'
import { Route, Switch, useParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { useMedia } from 'use-media'

import { Box, Button, Icon, HR, mediaQueries } from '@redriverpress/system'

import CategoryChip from '../../../common/components/CategoryChip'

import useVideoGenre from '../../hooks/useVideoGenre'
import useVideoGenres from '../../hooks/useVideoGenres'
import useVideoCategory from '../../hooks/useVideoCategory'
import GenreHeader from './GenreHeader'
import VideosSkeleton from '../skeletons/VideosSkeleton'
import VideosGrid from '../VideosGrid'
import VideoCard from '../VideoCard'
import CategoryHeader from './CategoryHeader'
import { GENRE_AND_CATEGORY_PATH, GENRE_PATH } from '../../constants/routes'
import VideoAccessDialog from '../../../common/components/VideoAccessDialog'

const GENRE_CHUNK = 36

export default function Genre({ userLoggedIn, userHasAccess }) {
  const isMd = useMedia(mediaQueries.md)
  const { genreSlug } = useParams()
  const { activeGenre } = useVideoGenres()
  const { isLoading: isLoadingGenre, genre, activeCategory } = useVideoGenre(
    activeGenre?.id
  )
  const { category } = useVideoCategory(activeCategory?.id)
  const [isAccessDialogOpen, setIsAccessDialogOpen] = useState(false)

  /**
   * Some genres might contain hundreds of videos; categories are usually < 100.
   * When showing all videos in a genre, only show 36 videos at a time,
   * to prevent performance slowdowns from React having to deal with hundreds of
   * videos (and their modals) all at once.
   */
  const [genreAmount, setGenreAmount] = useState(GENRE_CHUNK)

  const incrementGenreAmount = () =>
    setGenreAmount(amount => amount + GENRE_CHUNK)

  const chunkedGenreVideos = genre?.videos
    ? R.take(genreAmount, genre?.videos)
    : []

  useEffect(() => {
    // Reset genreAmount when switching genres
    setGenreAmount(GENRE_CHUNK)
  }, [genreSlug])

  return (
    <>
      <Box as='section'>
        <Route path={GENRE_PATH} exact={!isMd}>
          <GenreHeader />
        </Route>
        {isLoadingGenre || !genre ? (
          <VideosSkeleton />
        ) : (
          <Box>
            {isMd && (
              <>
                <Box
                  as='nav'
                  aria-label='Categories'
                  display='flex'
                  flexWrap='wrap'
                  m={-1}
                >
                  {genre.videoCategories.map(cat => (
                    <CategoryChip
                      key={cat.id}
                      to={`/videos/${genreSlug}/${cat.slug}`}
                      exact={cat.slug === ''}
                    >
                      {cat.title} ({cat.videosCount})
                    </CategoryChip>
                  ))}
                </Box>
                <HR my={4} borderColor='gray200' />
              </>
            )}
            <Switch>
              <Route path={GENRE_AND_CATEGORY_PATH}>
                <CategoryHeader />
                {category && (
                  <VideosGrid>
                    {category.videos.map(video => (
                      <VideoCard
                        key={video.id}
                        video={video}
                        isUserAllowed={userLoggedIn && userHasAccess}
                        openInfoModal={() => setIsAccessDialogOpen(true)}
                      />
                    ))}
                  </VideosGrid>
                )}
              </Route>

              <Route path={GENRE_PATH} exact>
                <VideosGrid>
                  {chunkedGenreVideos.map(video => (
                    <VideoCard
                      key={video.id}
                      video={video}
                      isUserAllowed={userLoggedIn && userHasAccess}
                      openInfoModal={() => setIsAccessDialogOpen(true)}
                    />
                  ))}
                </VideosGrid>

                {genreAmount < genre?.videos?.length && (
                  <Box textAlign='center' my={4}>
                    <Button onClick={incrementGenreAmount}>
                      View More
                      <Icon icon='arrow-down' pl={2} />
                    </Button>
                  </Box>
                )}
              </Route>
            </Switch>
          </Box>
        )}
      </Box>
      <VideoAccessDialog
        open={isAccessDialogOpen}
        onOpenChange={open => setIsAccessDialogOpen(open)}
        userLoggedIn={userLoggedIn}
      />
    </>
  )
}
